import type { AxiosError } from 'axios';
import axios from 'axios';
import qs from 'qs';
import { TokenUtil } from 'services/TokenUtil';
import { JSONAPIDeserializer } from './jsonapi-deserialiser';

/**
 * Axios instance configured for communicating with the ASSIST:API
 */
export const ASSIST_API = axios.create({
  baseURL: API_ENDPOINT_V2,
  headers: {
    'Content-Type': 'application/vnd.api+json'
  },
  paramsSerializer: (params) =>
    qs.stringify(params, {
      serializeDate: (date) => date.toUTCString(),
      encode: true
    })
});

/**
 * Combines params together, making them more suitable for use with ASSIST:API
 * @param base original or default parameters
 * @param override the params to prioritise / combine
 */
export const requestParams: (
  baseParams: RequestParams,
  overrideParams?: RequestParams
) => Record<string, unknown> = (base, { pagination, groups, include, filters, order } = {}) => ({
  ...base.pagination,
  ...pagination,
  pagination: !!base.pagination || !!pagination,
  ...(order || base.order),
  ...base.filters,
  ...filters,
  include: `${base.include ? `${base.include},` : ''}${include || ''}`,
  groups: [...(base.groups || []), ...(groups || [])]
});

/**
 * Request interceptor
 * Make some changes to the request before it is sent
 */
ASSIST_API.interceptors.request.use(
  (config) => {
    // Do something before request is sent
    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = `Bearer ${TokenUtil.getAccessToken()}`;
    return config;
  },
  (error) =>
    // Do something with request error
    Promise.reject(error)
);

/**
 * Improve axios error name
 *  - add status
 *  - add api url
 */
function processResponseError(error: AxiosError): Promise<never> {
  // eslint-disable-next-line prefer-promise-reject-errors
  return Promise.reject({
    ...error,
    name: `Axios: ${error?.response?.status} - ${error?.response?.config?.url}`
  });
}

/**
 * Response interceptor
 *  Modify the response when its received
 */
ASSIST_API.interceptors.response.use(
  async (response) =>
    !response.headers['content-type']?.includes('application/vnd.api+json')
      ? response
      : {
          ...response,
          data: response.data
            ? {
                links: response.data?.links,
                meta: response.data?.meta,
                json: await JSONAPIDeserializer.deserialize(response.data)
              }
            : {}
        },
  processResponseError
);

/**
 * Setup a default interceptor
 *  - improves generated error message
 */
axios.interceptors.response.use((response) => response, processResponseError);
